/**
 * useProjectChat -- Thin subscriber to the global streamManager.
 *
 * Each project has exactly one canonical chat (source='opus_build').
 * On mount: GET /api/v1/projects/:projectId/chats auto-creates if missing.
 *
 * Streaming ownership lives in streamManager (module-level singleton).
 * This hook:
 *   - Resolves the chatId via useProjectChatId
 *   - Provides the messages infinite query with dedup/merge
 *   - Detects server-says-streaming and calls streamManager.ensureStream
 *   - Delegates sendMessage / stopStream to the manager
 *   - Subscribes to the manager's slice for isStreaming, relayStatus, etc.
 *
 * NAVIGATION-SAFE: unmounting this hook does NOT abort the stream.
 * The manager keeps connections alive across navigation.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import { projectKeys } from '@/hooks/code/useProjects';
import { streamManager, OPTIMISTIC_MSG_ID_PREFIX } from '@/stores/streamManager';
import { useChatStreamSlice } from '@/stores/useStreamStore';
import { useProjectBusyStore } from '@/stores/useProjectBusyStore';
import type { Message, PaginatedResponse } from '@/types/chat.types';
import type { SendErrorInfo } from '@/components/chat/SendErrorBanner'; // FEAT-053 TASK-045

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const projectChatKeys = {
  all: ['project-chats'] as const,
  chat: (pid: string) => [...projectChatKeys.all, 'chat', pid] as const,
  messages: (cid: string) => [...projectChatKeys.all, 'messages', cid] as const,
};

// ---------------------------------------------------------------------------
// Types (re-exported for consumers)
// ---------------------------------------------------------------------------

/** Shape of the current_stream signal from the backend chat endpoint. */
interface CurrentStreamSignal {
  message_id: string;
  status: 'streaming';
  started_at: string;
}

interface ProjectChatResponse {
  id: string;
  project_id?: string;
  title: string | null;
  source?: string;
  model_id?: string | null;
  created_at: string;
  metadata?: Record<string, unknown>;
  current_stream?: CurrentStreamSignal | null;
}

export type RelayStatus = 'online' | 'offline';
export type ComplianceVerdict = 'allow' | 'flag' | 'redact' | 'block';

export interface ComplianceInfo {
  verdict: ComplianceVerdict;
  messageId: string;
  bannerText: string;
  redactedSpanCount?: number;
  ruleName?: string;
}

export interface UseProjectChatReturn {
  chatId: string | null;
  chatModelId: string | null;
  messages: Message[];
  isLoadingChat: boolean;
  isLoadingMessages: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isStreaming: boolean;
  isResumedStreaming: boolean;
  isAgentBusy: boolean;
  streamingMessage: Message | null;
  hasPendingAutoConnect: boolean;
  sendMessage: (content: string, attachmentIds?: string[], modelId?: string) => void;
  sendError: Error | null;
  // --- FEAT-053 TASK-045 (erro de envio) ---
  /** Clears `sendError` (banner dismiss). */
  clearSendError: () => void;
  /** Turn ended by the server (SSE error) before the reply started; null when none. By chat, lives in the slice. */
  turnError: SendErrorInfo | null;
  dismissTurnError: () => void;
  // --- fim FEAT-053 TASK-045 (erro de envio) ---
  stopStream: () => void;
  relayStatus: RelayStatus;
  complianceEvents: Map<string, ComplianceInfo>;
  error: Error | null;
  /** Manager-owned turn start timestamp. Continuous across navigation. */
  startedAt: number | null;
}

// ---------------------------------------------------------------------------
// Chat resolution query
// ---------------------------------------------------------------------------

function useProjectChatId(projectId: string | undefined) {
  return useQuery({
    queryKey: projectChatKeys.chat(projectId ?? ''),
    queryFn: async (): Promise<ProjectChatResponse> => {
      const result = await apiClient.get<
        | ProjectChatResponse
        | ProjectChatResponse[]
        | { data: ProjectChatResponse[]; next_cursor: string | null; has_more: boolean }
      >(`/v1/projects/${projectId}/chats`);

      const list: ProjectChatResponse[] | null = Array.isArray(result)
        ? result
        : result && typeof result === 'object' && 'data' in result && Array.isArray(result.data)
          ? result.data
          : null;

      if (list) {
        if (list.length > 0) return list[0]!;
        const created = await apiClient.post<ProjectChatResponse | { data: ProjectChatResponse }>(
          `/v1/projects/${projectId}/chats`,
          { source: 'opus_build' },
        );
        return created && typeof created === 'object' && 'data' in created
          ? created.data
          : (created as ProjectChatResponse);
      }

      return result as ProjectChatResponse;
    },
    enabled: !!projectId,
    staleTime: 5 * 60_000,
    retry: 2,
  });
}

// ---------------------------------------------------------------------------
// Messages infinite query
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10;

function useProjectChatMessages(
  chatId: string | null,
  refetchInterval: number | false,
) {
  return useInfiniteQuery({
    queryKey: projectChatKeys.messages(chatId ?? ''),
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (pageParam) qs.set('cursor', pageParam);
      qs.set('direction', 'before');
      qs.set('limit', String(PAGE_SIZE));
      return apiClient.get<PaginatedResponse<Message>>(
        `/v1/chats/${chatId}/messages?${qs.toString()}`,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p: PaginatedResponse<Message>) =>
      p.has_more ? (p.next_cursor ?? undefined) : undefined,
    enabled: !!chatId,
    staleTime: 60_000,
    refetchInterval,
  });
}

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

export function useProjectChat(projectId: string | undefined): UseProjectChatReturn {
  const qc = useQueryClient();
  const { data: chatData, isLoading: isLoadingChat, error: chatError } = useProjectChatId(projectId);
  const chatId = chatData?.id ?? null;
  const chatModelId = chatData?.model_id ?? null;
  const hasCurrentStream = !!(chatData?.current_stream);

  // ---------------------------------------------------------------------------
  // Subscribe to the manager's slice for this chat
  // ---------------------------------------------------------------------------
  const slice = useChatStreamSlice(chatId);
  const managerIsStreaming = slice.isStreaming;
  const managerIsSending = slice.isSending;

  // ---------------------------------------------------------------------------
  // Poll interval: only for resumed/background turns with no live SSE
  // ---------------------------------------------------------------------------
  const [pollInterval, setPollInterval] = useState<number | false>(false);

  const { data: md, isLoading: isLoadingMessages, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useProjectChatMessages(chatId, pollInterval);

  // ---------------------------------------------------------------------------
  // Message dedup/merge (KEPT: same logic as before, reconciles manager cache
  // writes with persisted query)
  // ---------------------------------------------------------------------------
  const rawMessages: Message[] = useMemo(() => {
    if (!md) return [];
    const all = md.pages
      .flatMap((p) => p.data)
      .filter((m) => m.role === 'user' || m.role === 'assistant');
    const byId = new Map<string, Message>();
    for (const m of all) {
      const existing = byId.get(m.id);
      if (!existing) {
        byId.set(m.id, m);
      } else {
        if (m.status === 'streaming' && existing.status !== 'streaming') {
          byId.set(m.id, m);
        } else if (existing.status === 'streaming' && m.status !== 'streaming') {
          // keep existing
        } else if (m.status === 'streaming' && existing.status === 'streaming') {
          if (m.content_blocks.length > existing.content_blocks.length) {
            byId.set(m.id, m);
          }
        } else if (
          m.sequence_number === Number.MAX_SAFE_INTEGER &&
          existing.sequence_number !== Number.MAX_SAFE_INTEGER
        ) {
          byId.set(m.id, m);
        }
      }
    }
    return Array.from(byId.values()).sort(
      (a, b) => a.sequence_number - b.sequence_number,
    );
  }, [md]);

  // ---------------------------------------------------------------------------
  // Server-state derivations (KEPT)
  // ---------------------------------------------------------------------------
  const persisted = rawMessages.filter((m) => m.sequence_number !== Number.MAX_SAFE_INTEGER);
  const lastPersistedAssistant = useMemo(() => {
    for (let i = persisted.length - 1; i >= 0; i--) {
      if (persisted[i]!.role === 'assistant') return persisted[i]!;
    }
    return null;
  }, [persisted]);

  // --- FEAT-053 TASK-045 (QA-039-A) ---
  // The message the auto-connect effect reasons about must be a row the SERVER accepted. The optimistic user
  // message streamManager.send appends before the POST (and removes if refused) is NOT one: counting it made the
  // effect see "user row awaiting an answer" and open GET /stream concurrently with the POST (409 against its own
  // connection) and, after a refusal, leave the connection -- dots bubble + Stop -- behind.
  const latestPersistedMessage = useMemo(() => {
    for (let i = persisted.length - 1; i >= 0; i--) {
      if (!persisted[i]!.id.startsWith(OPTIMISTIC_MSG_ID_PREFIX)) return persisted[i]!;
    }
    return null;
  }, [persisted]);
  // --- fim FEAT-053 TASK-045 (QA-039-A) ---

  const serverTurnTerminal = lastPersistedAssistant !== null && lastPersistedAssistant !== undefined &&
    (lastPersistedAssistant.status === 'completed' || lastPersistedAssistant.status === 'errored' || lastPersistedAssistant.status === 'cancelled');
  const serverStreaming = lastPersistedAssistant?.status === 'streaming';

  const cachedStreamingAssistant = useMemo((): Message | null => {
    for (let i = rawMessages.length - 1; i >= 0; i--) {
      if (rawMessages[i]!.role === 'assistant' && rawMessages[i]!.status === 'streaming') return rawMessages[i]!;
    }
    return null;
  }, [rawMessages]);

  // Rendered messages: drop stale sentinel placeholders when server says terminal
  const messages: Message[] = serverTurnTerminal ? persisted : rawMessages;

  const isResumedStreaming = !!serverStreaming && !managerIsStreaming;

  const isAgentBusy = managerIsStreaming || isResumedStreaming || managerIsSending
    || (hasCurrentStream && !serverTurnTerminal)
    || (!!cachedStreamingAssistant && !serverTurnTerminal);

  // ---------------------------------------------------------------------------
  // SIDEBAR SYNC: push busy state into legacy Zustand store
  // ---------------------------------------------------------------------------
  const setBusy = useProjectBusyStore((s) => s.setBusy);
  const clearBusy = useProjectBusyStore((s) => s.clear);

  useEffect(() => {
    if (!projectId) return;
    setBusy(projectId, isAgentBusy);
  }, [projectId, isAgentBusy, setBusy]);

  useEffect(() => {
    const pid = projectId;
    return () => { if (pid) clearBusy(pid); };
  }, [projectId, clearBusy]);

  // ---------------------------------------------------------------------------
  // PROJECT-LIST INVALIDATION on turn lifecycle transitions
  // ---------------------------------------------------------------------------
  const prevIsAgentBusyRef = useRef(isAgentBusy);
  useEffect(() => {
    const prev = prevIsAgentBusyRef.current;
    prevIsAgentBusyRef.current = isAgentBusy;
    if (prev !== isAgentBusy) {
      void qc.invalidateQueries({ queryKey: projectKeys.lists() });
      if (projectId) {
        void qc.invalidateQueries({ queryKey: projectChatKeys.chat(projectId) });
      }
    }
  }, [isAgentBusy, projectId, qc]);

  // ---------------------------------------------------------------------------
  // Clear isSending when server surfaces a NEW terminal assistant turn
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!managerIsSending) return;
    if (!chatId) return;
    const la = lastPersistedAssistant;
    const newTurnTerminal = !!la &&
      (la.status === 'completed' || la.status === 'errored' || la.status === 'cancelled');
    if (newTurnTerminal) {
      streamManager.clearSending(chatId);
    }
  }, [managerIsSending, lastPersistedAssistant, chatId]);

  // ---------------------------------------------------------------------------
  // Poll at 2.5s ONLY for resumed/background turns with no live SSE
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setPollInterval(isResumedStreaming && !managerIsStreaming && !managerIsSending ? 2500 : false);
  }, [isResumedStreaming, managerIsSending, managerIsStreaming]);

  // ---------------------------------------------------------------------------
  // Safety valve: if manager says streaming but server's turn is terminal,
  // force-clear it
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (managerIsStreaming && serverTurnTerminal && chatId && !streamManager.hasActiveConnection(chatId)) {
      // No actual connection -- the store is stale. Clear it.
      streamManager.clearSending(chatId);
    }
  }, [managerIsStreaming, serverTurnTerminal, chatId]);

  // The streaming message: last assistant with status='streaming' in messages
  const streamingMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]!;
      if (m.role === 'assistant' && m.status === 'streaming') return m;
    }
    return null;
  }, [messages]);

  // ---------------------------------------------------------------------------
  // AUTO-RESUME: detect server-says-streaming and call ensureStream
  // (ONE connect path -- the manager's NO-OP guard prevents double-open)
  // ---------------------------------------------------------------------------
  const hasPendingStream = !!(chatData?.metadata?._pending_stream);

  // Track whether the auto-connect effect has already fired for this chatId.
  // Prevents infinite reconnect loops when the manager exhausts its reconnect
  // budget and the effect re-fires because slice.status changed. Reset on
  // chatId change and on sendMessage (fresh send always works).
  const autoConnectedChatRef = useRef<string | null>(null);

  useEffect(() => {
    if (!chatId || !projectId) return;
    if (isLoadingChat) return;
    // Don't auto-connect if manager already has a live connection
    if (streamManager.hasActiveConnection(chatId)) return;
    // One-shot guard: if we already auto-connected for this chatId, don't
    // re-fire. ensureStream is idempotent when a connection exists, but after
    // teardown (reconnect exhaustion) we must NOT loop back into ensureStream.
    if (autoConnectedChatRef.current === chatId) return;

    const latestIsTerminalAssistant = latestPersistedMessage !== null && latestPersistedMessage !== undefined &&
      latestPersistedMessage.role === 'assistant' &&
      (latestPersistedMessage.status === 'completed' ||
       latestPersistedMessage.status === 'errored' ||
       latestPersistedMessage.status === 'cancelled');

    if (latestIsTerminalAssistant && !hasPendingStream && !cachedStreamingAssistant) {
      return;
    }

    const latestIsAwaitingUser = latestPersistedMessage !== null && latestPersistedMessage !== undefined &&
      latestPersistedMessage.role === 'user';
    const shouldAutoConnect = hasPendingStream || latestIsAwaitingUser || serverStreaming
      || !!cachedStreamingAssistant
      || (hasCurrentStream && lastPersistedAssistant?.status === 'streaming');
    const shouldAutoConnectEagerly = hasCurrentStream && !latestIsTerminalAssistant;

    if (!shouldAutoConnect && !shouldAutoConnectEagerly) return;

    autoConnectedChatRef.current = chatId;

    // Derive startedAtHint: prefer current_stream.started_at (freshest),
    // fall back to the cached streaming assistant's created_at (covers
    // navigate-back where React Query serves stale current_stream=null).
    const startedAtHint = chatData?.current_stream?.started_at
      ? Date.parse(chatData.current_stream.started_at)
      : (cachedStreamingAssistant?.created_at
          ? Date.parse(cachedStreamingAssistant.created_at)
          : undefined);

    streamManager.ensureStream(chatId, projectId, {
      startedAtHint,
    });
  }, [chatId, projectId, isLoadingChat, hasCurrentStream, hasPendingStream, serverStreaming, cachedStreamingAssistant, lastPersistedAssistant, latestPersistedMessage, chatData?.current_stream?.started_at]);

  // ---------------------------------------------------------------------------
  // sendMessage -- delegates to manager
  // ---------------------------------------------------------------------------
  const [sendError, setSendError] = useState<Error | null>(null);
  // --- FEAT-053 TASK-045 (erro de envio) ---
  // The warning of one project must not show up in another.
  useEffect(() => {
    setSendError(null);
  }, [chatId]);
  const clearSendError = useCallback(() => setSendError(null), []);
  const dismissTurnError = useCallback(() => {
    if (chatId) streamManager.clearTurnError(chatId);
  }, [chatId]);
  // --- fim FEAT-053 TASK-045 (erro de envio) ---

  const sendMessage = useCallback((c: string, a?: string[], modelId?: string) => {
    if (!chatId || !projectId || isAgentBusy) return;
    setSendError(null);
    // --- FEAT-053 TASK-045 (QA-039-A) ---
    // A send owns its connection: streamManager.send calls ensureStream itself, after the POST was accepted. The
    // auto-connect effect must therefore NOT also connect on account of this send (it used to, as soon as the
    // optimistic row was appended: GET /stream concurrent with the POST, left open after a refusal). Marking the
    // one-shot as consumed keeps the previous guarantee that the effect cannot loop back into ensureStream after
    // the manager exhausts its reconnect budget. (Before, the early connect of the effect consumed it by accident;
    // resetting it to null here, with no early connect, would re-open that loop.)
    autoConnectedChatRef.current = chatId;
    // --- fim FEAT-053 TASK-045 (QA-039-A) ---
    void streamManager.send(chatId, projectId, {
      content: c,
      attachment_ids: a,
      model_id: modelId,
    }).catch((err: unknown) => {
      if (err instanceof Error) setSendError(err);
    });
  }, [chatId, projectId, isAgentBusy]);

  const stopStream = useCallback(() => {
    if (chatId) streamManager.stop(chatId);
  }, [chatId]);

  return {
    chatId,
    chatModelId,
    messages,
    isLoadingChat,
    isLoadingMessages,
    isLoadingMore: isFetchingNextPage,
    hasMore: hasNextPage ?? false,
    loadMore: useCallback(() => { void fetchNextPage(); }, [fetchNextPage]),
    isStreaming: managerIsStreaming,
    isResumedStreaming,
    isAgentBusy,
    streamingMessage,
    hasPendingAutoConnect: slice.hasPendingConnect,
    sendMessage,
    sendError,
    clearSendError, // FEAT-053 TASK-045
    turnError: slice.turnError, // FEAT-053 TASK-045
    dismissTurnError, // FEAT-053 TASK-045
    stopStream,
    relayStatus: slice.relayStatus,
    complianceEvents: slice.complianceEvents,
    error: (chatError as Error) ?? null,
    startedAt: slice.startedAt,
  };
}
